// src/pages/AdminStore.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { safeUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const font = { mono: '"JetBrains Mono", monospace', condensed: '"Barlow Condensed", sans-serif', body: '"Barlow", sans-serif' };
const EMPTY = { name: '', description: '', price: '', image_url: '', category: 'Apparel', sizes: '', colors: '', in_stock: true };
const CATS  = ['Apparel', 'Gaming Gear', 'Accessories', 'Digital', 'Other'];

const AdminStore = () => {
  const { authToken } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState('');

  const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/products`, { headers })
      .then(r => r.json()).then(d => setProducts(d.products || []))
      .catch(() => showToast('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit   = p => { setEditing(p); setForm({ name: p.name, description: p.description||'', price: String(p.price), image_url: p.image_url||'', category: p.category||'Apparel', sizes: (p.sizes||[]).join(', '), colors: (p.colors||[]).join(', '), in_stock: p.in_stock !== false }); setModal(true); };

  const save = async () => {
    if (!form.name.trim() || !form.price || isNaN(parseFloat(form.price))) { showToast('Name and valid price required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), sizes: form.sizes.split(',').map(s=>s.trim()).filter(Boolean), colors: form.colors.split(',').map(s=>s.trim()).filter(Boolean) };
      const url    = editing ? `${BACKEND_URL}/api/admin/products/${editing.id}` : `${BACKEND_URL}/api/admin/products`;
      const method = editing ? 'PUT' : 'POST';
      const res    = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error();
      showToast(editing ? '✓ Product updated' : '✓ Product created');
      setModal(false); load();
    } catch { showToast('Failed to save'); }
    finally { setSaving(false); }
  };

  const del = async id => {
    if (!window.confirm('Delete this product?')) return;
    const res = await fetch(`${BACKEND_URL}/api/admin/products/${id}`, { method: 'DELETE', headers });
    if (res.ok) { showToast('Deleted'); load(); }
  };

  const S = {
    page:     { padding: '2.5rem' },
    btn:      { background: '#fff', color: '#000', border: 'none', padding: '0.58rem 1.4rem', fontFamily: font.mono, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 },
    btnGhost: { background: 'none', border: '1px solid #1a1a1a', color: '#666', padding: '0.3rem 0.75rem', fontFamily: font.mono, fontSize: '0.6rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
    input:    { background: '#060606', border: '1px solid #1a1a1a', color: '#fff', padding: '0.58rem 0.85rem', fontFamily: font.mono, fontSize: '0.7rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
    label:    { fontFamily: font.mono, fontSize: '0.55rem', color: '#444', letterSpacing: '0.12em', marginBottom: 5, display: 'block', textTransform: 'uppercase' },
  };

  return (
    <div style={S.page}>
      <style>{`@keyframes fadein{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}} .overlay{position:fixed;inset:0;background:rgba(0,0,0,0.9);z-index:200;display:flex;align-items:flex-start;justify-content:center;padding:2rem;overflow-y:auto;} select option{background:#060606;}`}</style>

      {toast && <div style={{ position:'fixed', bottom:'1.5rem', right:'1.5rem', background:'#111', border:'1px solid #222', color:'#fff', fontFamily:font.mono, fontSize:'0.7rem', padding:'0.75rem 1.25rem', zIndex:999, animation:'fadein 0.2s ease' }}>{toast}</div>}

      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:'1.5rem' }}>
        <div>
          <div style={{ fontFamily:font.mono, fontSize:'0.58rem', color:'#333', letterSpacing:'0.2em', marginBottom:6 }}>// MANAGE</div>
          <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'2.5rem', letterSpacing:'-0.02em' }}>Store</div>
        </div>
        <button style={S.btn} onClick={openCreate}><Plus size={13}/> NEW PRODUCT</button>
      </div>

      {loading ? <p style={{ fontFamily:font.mono, fontSize:'0.68rem', color:'#333' }}>LOADING...</p> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:'1rem' }}>
          {products.length === 0 && <p style={{ fontFamily:font.mono, fontSize:'0.68rem', color:'#333' }}>No products yet.</p>}
          {products.map(p => (
            <div key={p.id} style={{ background:'#0a0a0a', border:'1px solid #111' }}>
              {p.image_url && safeUrl(p.image_url) && <img src={safeUrl(p.image_url)} alt={p.name} style={{ width:'100%', height:150, objectFit:'cover', display:'block', filter:'grayscale(60%)' }}/>}
              <div style={{ padding:'0.9rem' }}>
                <div style={{ fontFamily:font.body, fontSize:'0.88rem', color:'#ddd', marginBottom:2 }}>{p.name}</div>
                <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'1.2rem', marginBottom:5 }}>${p.price}</div>
                <div style={{ fontFamily:font.mono, fontSize:'0.58rem', color:'#333', marginBottom:'0.75rem' }}>{p.category} · {p.in_stock ? 'In Stock' : 'Out of Stock'}</div>
                <div style={{ display:'flex', gap:5 }}>
                  <button style={S.btnGhost} onClick={() => openEdit(p)}><Pencil size={10}/> Edit</button>
                  <button style={{ ...S.btnGhost, color:'#ff6b6b', borderColor:'#2a1010' }} onClick={() => del(p.id)}><Trash2 size={10}/> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div style={{ background:'#0a0a0a', border:'1px solid #1a1a1a', padding:'2rem', width:'100%', maxWidth:560 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
              <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'1.4rem' }}>{editing ? 'EDIT PRODUCT' : 'NEW PRODUCT'}</div>
              <button onClick={() => setModal(false)} style={{ background:'none', border:'none', color:'#555', cursor:'pointer' }}><X size={16}/></button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.9rem' }}>
              <div><label style={S.label}>Name *</label><input style={S.input} value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Product name"/></div>
              <div><label style={S.label}>Description</label><textarea style={{ ...S.input, height:80, resize:'vertical' }} value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Description..."/></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div><label style={S.label}>Price * ($)</label><input style={S.input} type="number" step="0.01" value={form.price} onChange={e=>setForm(p=>({...p,price:e.target.value}))} placeholder="29.99"/></div>
                <div><label style={S.label}>Category</label><select style={S.input} value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>{CATS.map(c=><option key={c}>{c}</option>)}</select></div>
              </div>
              <div><label style={S.label}>Image URL</label><input style={S.input} value={form.image_url} onChange={e=>setForm(p=>({...p,image_url:e.target.value}))} placeholder="https://..."/></div>
              <div><label style={S.label}>Sizes (comma separated)</label><input style={S.input} value={form.sizes} onChange={e=>setForm(p=>({...p,sizes:e.target.value}))} placeholder="S, M, L, XL"/></div>
              <div><label style={S.label}>Colors (comma separated)</label><input style={S.input} value={form.colors} onChange={e=>setForm(p=>({...p,colors:e.target.value}))} placeholder="Black, White"/></div>
              <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontFamily:font.mono, fontSize:'0.62rem', color:'#666' }}><input type="checkbox" checked={form.in_stock} onChange={e=>setForm(p=>({...p,in_stock:e.target.checked}))}/> In Stock</label>
            </div>
            <div style={{ display:'flex', gap:'0.75rem', marginTop:'1.5rem' }}>
              <button style={S.btn} onClick={save} disabled={saving}>{saving ? 'SAVING...' : editing ? 'UPDATE' : 'CREATE'}</button>
              <button style={{ ...S.btn, background:'#111', color:'#666', border:'1px solid #1a1a1a' }} onClick={() => setModal(false)}>CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStore;